import { useEffect } from "react";
import { createBrowserRouter, Outlet, type RouteObject, useLocation } from "react-router-dom";

import { toApex25AppUrl } from "@/lib/apex25AppUrl";
import { Apex25Page } from "@/pages/apex25/Apex25Page";
import { APEX25_LEGAL_PAGES } from "@/pages/apex25/industries";
import { apex25IndustryRoutes } from "@/pages/apex25/industry-routes";
import { Apex25LegalRoute } from "@/pages/apex25/legal/page";
import { AnalyticsProvider } from "@/providers/analytics";
import { ThemeProvider } from "@/providers/theme";

/**
 * Any other path on a marketing host is sent to the same path on recruit.theresume.io, where Clerk
 * and the rest of the app actually run. The server 301s full page loads; this covers in-app links.
 */
const LeaveMarketingHost = () => {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    window.location.replace(toApex25AppUrl(`${pathname}${search}${hash}`));
  }, [pathname, search, hash]);

  return null;
};

// No Clerk-dependent providers here (NotificationProvider reads the signed-in user), and nothing
// in this route tree imports Clerk: the marketing hosts run without it.
export const apex25MarketingRoutes: RouteObject[] = [
  {
    element: (
      <ThemeProvider>
        <AnalyticsProvider>
          <Outlet />
        </AnalyticsProvider>
      </ThemeProvider>
    ),
    children: [
      { path: "/", element: <Apex25Page /> },
      ...apex25IndustryRoutes(),
      // The legal set: /legal, /terms, /privacy, /candidate-terms, /trial-terms.
      ...Object.keys(APEX25_LEGAL_PAGES).map((path) => ({
        path,
        element: <Apex25LegalRoute />,
      })),
      { path: "*", element: <LeaveMarketingHost /> },
    ],
  },
];

export const createApex25MarketingRouter = () => createBrowserRouter(apex25MarketingRoutes);
